import React, { lazy, Suspense, ComponentType } from 'react';

import { Box } from '../components/box';
import { Spinner } from '../components/spinner';
import { Button } from '../components/button';
import { VStack } from '../components/stack';
import { Text } from '../components/text';

import { SimpleErrorBoundary } from './simple-error-boundary';

// Our own FallbackProps interface
interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

/**
 * Options for lazy loading components
 */
export interface LazyLoadOptions {
  /** Custom loading component */
  fallback?: React.ReactNode;
  /** Custom error component */
  errorFallback?: ComponentType<FallbackProps>;
  /** Retry attempts on error */
  retryAttempts?: number;
  /** Delay before showing loading (prevents flash) */
  loadingDelay?: number;
  /** Minimum loading time (prevents flash) */
  minLoadingTime?: number;
  /** Prefetch the component */
  prefetch?: boolean;
  /** Chunk name for webpack */
  chunkName?: string;
}

/**
 * Default loading component
 */
const DefaultLoadingComponent: React.FC<{ message?: string }> = ({ message = 'Yükleniyor...' }) => (
  <Box display="flex" alignItems="center" justifyContent="center" minHeight="200px" width="100%">
    <VStack gap={4}>
      <Spinner size="xl" />
      <Text color="fg.muted">{message}</Text>
    </VStack>
  </Box>
);

/**
 * Default error component
 */
const DefaultErrorComponent: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    minHeight="200px"
    width="100%"
    padding={4}
  >
    <VStack gap={4} align="center">
      <Text color="fg.error" fontWeight="bold">
        Failed to load component
      </Text>
      <Text color="fg.muted" fontSize="sm" textAlign="center">
        {error?.message || 'An unexpected error occurred'}
      </Text>
      <Button onClick={resetErrorBoundary} size="sm">
        Try Again
      </Button>
    </VStack>
  </Box>
);

/**
 * Enhanced lazy loading with error boundary and loading states
 */
export function lazyLoad<T extends ComponentType>(
  importFn: () => Promise<{ default: T }>,
  options: LazyLoadOptions = {}
): React.ComponentType<Record<string, unknown>> {
  const {
    fallback = <DefaultLoadingComponent />,
    errorFallback = DefaultErrorComponent,
    retryAttempts = 3,
    loadingDelay = 200,
    minLoadingTime = 300,
    prefetch = false,
    chunkName,
  } = options;

  // Create lazy component with proper retry logic for React 19
  const retryState = { attempts: 0 };

  const createImportFn = (): (() => Promise<{ default: React.ComponentType<unknown> }>) => {
    return () => {
      const startTime = Date.now();

      return importFn()
        .then((result) => {
          if (result && typeof result === 'object' && 'default' in result) {
            const module = result as { default: React.ComponentType<unknown> };
            retryState.attempts = 0; // Reset on success

            // Ensure minimum loading time to prevent flash
            const loadTime = Date.now() - startTime;
            if (loadTime < minLoadingTime) {
              return new Promise<{ default: React.ComponentType<unknown> }>((resolve) => {
                setTimeout(() => resolve(module), minLoadingTime - loadTime);
              });
            }
            return module;
          }
          throw new Error('Invalid module format');
        })
        .catch((error) => {
          if (retryState.attempts < retryAttempts) {
            retryState.attempts++;
            // Recursive retry with exponential backoff
            return new Promise<{ default: React.ComponentType<unknown> }>((resolve, reject) => {
              setTimeout(() => {
                createImportFn()().then(resolve).catch(reject);
              }, 100 * retryState.attempts);
            });
          }
          throw error;
        });
    };
  };

  const LazyComponent = lazy(createImportFn());

  // Prefetch component if requested (setTimeout fallback for Safari < 18.4)
  if (prefetch && typeof window !== 'undefined') {
    const scheduleIdle =
      typeof requestIdleCallback === 'function'
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 1);
    scheduleIdle(() => {
      importFn().catch(() => {
        // Ignore prefetch errors
      });
    });
  }

  // Create wrapper component with error boundary and suspense
  // Create wrapper component with error boundary and suspense
  const LazyLoadWrapper = React.forwardRef<HTMLElement, Record<string, unknown>>((props, _ref) => {
    const [showLoading, setShowLoading] = React.useState(false);

    React.useEffect(() => {
      const timer = setTimeout(() => {
        setShowLoading(true);
      }, loadingDelay);

      return () => clearTimeout(timer);
    }, []);

    const fallbackElement = React.createElement(errorFallback, {
      error: new Error('Component failed to load'),
      resetErrorBoundary: () => {
        retryState.attempts = 0;
        window.location.reload();
      },
    });

    return React.createElement(SimpleErrorBoundary, {
      fallback: fallbackElement,
      children: React.createElement(
        Suspense,
        { fallback: showLoading ? fallback : null },
        React.createElement(LazyComponent, props)
      ),
    });
  });

  LazyLoadWrapper.displayName = `LazyLoad(${chunkName || 'Component'})`;

  return LazyLoadWrapper as React.ComponentType<Record<string, unknown>>;
}

/**
 * Create a lazy loaded route component
 */
export function lazyRoute<T extends ComponentType<Record<string, unknown>>>(
  importFn: () => Promise<{ default: T }>,
  options?: LazyLoadOptions
): React.ComponentType<Record<string, unknown>> {
  return lazyLoad(importFn, {
    ...options,
    fallback: options?.fallback || (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        width="100%"
      >
        <VStack gap={4}>
          <Spinner size="xl" />
          <Text color="fg.muted">Sayfa yükleniyor...</Text>
        </VStack>
      </Box>
    ),
  });
}

/**
 * Intersection Observer based lazy loading
 */
export function LazyLoadOnView<T extends ComponentType>({
  component,
  importFn,
  options = {},
  rootMargin = '50px',
  threshold = 0.1,
  placeholder,
  ...props
}: {
  component?: T;
  importFn?: () => Promise<{ default: T }>;
  options?: LazyLoadOptions;
  rootMargin?: string;
  threshold?: number | number[];
  placeholder?: React.ReactNode;
} & Record<string, unknown>) {
  const [Component, setComponent] = React.useState<T | null>(null);
  const [isInView, setIsInView] = React.useState(false);
  const elementRef = React.useRef<HTMLDivElement>(null);

  // Use component prop directly if available, otherwise use state
  const activeComponent = component || Component;

  React.useEffect(() => {
    if (activeComponent || !importFn) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
        }
      },
      { rootMargin, threshold }
    );

    const element = elementRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
      observer.disconnect();
    };
  }, [activeComponent, importFn, rootMargin, threshold]);

  React.useEffect(() => {
    if (!isInView || activeComponent || !importFn) return;

    importFn().then((module: { default: T }) => {
      setComponent(module.default);
    });
  }, [isInView, activeComponent, importFn]);

  if (!activeComponent) {
    return (
      <div ref={elementRef}>{placeholder || options.fallback || <DefaultLoadingComponent />}</div>
    );
  }

  const {
    component: _component,
    importFn: _importFn,
    options: _options,
    rootMargin: _rootMargin,
    threshold: _threshold,
    placeholder: _placeholder,
    ...componentProps
  } = props;

  // React 19 compatible component rendering with proper type checking
  if (activeComponent && typeof activeComponent === 'function') {
    return React.createElement(activeComponent, componentProps);
  }

  // Fallback for invalid component
  return (
    <div ref={elementRef}>
      <Text color="fg.error">Invalid component provided</Text>
    </div>
  );
}

/**
 * Preload a component
 */
export function preloadComponent<T extends ComponentType>(
  importFn: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  return importFn();
}

/**
 * Batch preload multiple components
 */
export function preloadComponents(
  components: Array<() => Promise<{ default: ComponentType }>>
): Promise<void> {
  return Promise.all(components.map(preloadComponent)).then(() => undefined);
}

/**
 * Create a prefetcher hook
 */
export function usePrefetch<T extends ComponentType>(
  importFn: () => Promise<{ default: T }>,
  options: { delay?: number; onHover?: boolean } = {}
) {
  const { delay = 0, onHover = false } = options;
  const [isPrefetched, setIsPrefetched] = React.useState(false);

  const prefetch = React.useCallback(() => {
    if (isPrefetched) return;

    const doPrefetch = () => {
      importFn()
        .then(() => setIsPrefetched(true))
        .catch(() => {
          // Ignore prefetch errors
        });
    };

    if (delay > 0) {
      setTimeout(doPrefetch, delay);
    } else {
      doPrefetch();
    }
  }, [importFn, isPrefetched, delay]);

  const prefetchProps = onHover
    ? {
        onMouseEnter: prefetch,
        onFocus: prefetch,
      }
    : {};

  return { prefetch, prefetchProps, isPrefetched };
}

/**
 * Resource hints for lazy loaded components
 */
export function ResourceHint({
  href,
  as = 'script',
  type = 'prefetch',
}: {
  href: string;
  as?: 'script' | 'style' | 'image' | 'font';
  type?: 'prefetch' | 'preload' | 'dns-prefetch' | 'preconnect';
}) {
  React.useEffect(() => {
    const link = document.createElement('link');
    link.rel = type;
    link.href = href;

    // Only set 'as' attribute for types that support it
    if (type === 'prefetch' || type === 'preload') {
      link.setAttribute('as', as);
    }

    if (type === 'preload') {
      link.crossOrigin = 'anonymous';
    }

    document.head.appendChild(link);

    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, [href, as, type]);

  return null;
}
